import {useContext, useEffect} from 'react';
import {NavigationFocusContext} from '@header/scripts/components/context/NavigationFocusContext.jsx';
import whatInput from 'what-input';

const SECOND_NAVI_LEVEL = 'second';
const NAVIGATION_CONTAINER_SELECTOR = '.navigation__container-new';

/**
 * @param contentKey re-runs the effect when the content of the level is replaced, e.g. after it was fetched
 */
export const useNaviFocusTrap = (containerRef, level, closeLayer, disableFocusTrap = false, contentKey = null) => {
    const {navigationFocus, setFocusedLevel, setFocusFirst, reset} = useContext(NavigationFocusContext);

    const focusOut = () => {
        closeLayer();
        reset();
    };

    useEffect(() => {
        if (disableFocusTrap) return;

        const inputType = whatInput.ask('intent');
        if (inputType !== 'keyboard') return;

        if (navigationFocus.focusedLevel !== level) return;

        const container = containerRef.current;
        if (!container) return;

        // the content of a level can be replaced while it is still loading, which drops the focus to the body
        const focusWasLost = document.activeElement === document.body;
        if (navigationFocus.focusFirst || focusWasLost) {
            getFocusableElements(container)[0]?.focus();
            setFocusFirst(false);
        }

        function handleKeyDown(e) {
            if (e.key !== 'Tab') return;

            // the layer content changes while navigating, so the elements cannot be cached
            const focusables = getFocusableElements(container);
            const active = document.activeElement;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const triggerElement =
                level === SECOND_NAVI_LEVEL
                    ? navigationFocus.firstLevelTriggerElement
                    : navigationFocus.secondLevelTriggerElement;

            if (e.shiftKey && active === first) {
                e.preventDefault();
                triggerElement?.focus();
                if (level === SECOND_NAVI_LEVEL) {
                    focusOut();
                }
                setFocusedLevel(SECOND_NAVI_LEVEL);
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                focusOut();
                focusNextOutsideNavigation();
            }
        }

        container.addEventListener('keydown', handleKeyDown);
        return () => container.removeEventListener('keydown', handleKeyDown);
    }, [navigationFocus, closeLayer, contentKey]);
};

function getFocusableElements(container) {
    if (!container) return [];
    // a[href] instead of [href], otherwise the svg <use href="..."> of an icon is treated as focusable
    const focusableSelectors = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    return Array.from(container.querySelectorAll(focusableSelectors)).filter(
        (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'),
    );
}

/**
 * Leaves the whole navigation instead of continuing inside of it, because everything that follows the
 * trigger element belongs to the layer that is closing right now.
 */
function focusNextOutsideNavigation() {
    const navigation = document.querySelector(NAVIGATION_CONTAINER_SELECTOR);
    if (!navigation) return;

    const candidates = getFocusableElements(document.body).filter(
        (element) =>
            !navigation.contains(element) &&
            navigation.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING,
    );

    // an element that cannot take the focus, e.g. a hidden one, is skipped
    for (const candidate of candidates) {
        candidate.focus();
        if (document.activeElement === candidate) return;
    }
}
